import { useEffect, useRef } from 'react';
import { meterScale, subscribeMeter } from '@/state/meters';

/**
 * A channel meter. It writes directly to DOM style properties from an
 * animation frame rather than re-rendering, so a mixer full of moving meters
 * costs no React work at all.
 */
export function Meter({ trackId, label }: { trackId: string; label: string }): JSX.Element {
  const fillRef = useRef<HTMLDivElement>(null);
  const clipRef = useRef<HTMLDivElement>(null);
  const peakRef = useRef<HTMLDivElement>(null);

  useEffect(
    () =>
      subscribeMeter(trackId, (reading, heldPeak) => {
        const fill = fillRef.current;
        if (fill) fill.style.transform = `scaleY(${meterScale(Math.max(reading.rmsLeft, reading.rmsRight))})`;
        const peak = peakRef.current;
        if (peak) peak.style.bottom = `${meterScale(heldPeak) * 100}%`;
        const clip = clipRef.current;
        if (clip) clip.className = reading.clipped ? 'meter__clip meter__clip--on' : 'meter__clip';
      }),
    [trackId],
  );

  return (
    <div className="meter" role="meter" aria-label={`${label} output level`} aria-live="off">
      <div ref={fillRef} className="meter__fill" style={{ top: 0, height: '100%', transform: 'scaleY(0)' }} />
      <div
        ref={peakRef}
        style={{ position: 'absolute', left: 0, right: 0, height: '1px', background: 'rgba(255,255,255,0.65)', bottom: 0 }}
      />
      <div ref={clipRef} className="meter__clip" />
    </div>
  );
}
